// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, AlertTriangle } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [mfa, setMfa] = useState(null); // {challenge_token, email}
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // StrictMode safety: process exactly once
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';

    // Phase 44 · OIDC enterprise SSO — backend redirects here with
    //   #apex_token=<jwt>&email=<email>
    const oidcMatch = hash.match(/apex_token=([^&]+)/);
    if (oidcMatch) {
      const token = decodeURIComponent(oidcMatch[1]);
      const emailMatch = hash.match(/email=([^&]+)/);
      const email = emailMatch ? decodeURIComponent(emailMatch[1]) : null;
      (async () => {
        try {
          localStorage.setItem('apex_token', token);
          const { data: me } = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          localStorage.setItem('apex_user', JSON.stringify(me.user || me));
          window.history.replaceState({}, '', '/');
          navigate('/', { replace: true });
        } catch (e) {
          console.error('OIDC token adoption failed', e);
          if (email) {
            localStorage.setItem('apex_user', JSON.stringify({ email, role: 'operator' }));
          }
          window.history.replaceState({}, '', '/');
          navigate('/', { replace: true });
        }
      })();
      return;
    }

    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = match[1];

    (async () => {
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        if (data?.mfa_required) {
          // SSO LOCKDOWN: TOTP step required before any session issues
          window.history.replaceState({}, '', '/');
          setMfa({ challenge_token: data.challenge_token, email: data.email });
          return;
        }
        if (data?.user) {
          localStorage.setItem('apex_user', JSON.stringify(data.user));
          localStorage.removeItem('apex_token'); // using cookie session now
        }
        window.history.replaceState({}, '', '/');
        navigate('/', { replace: true, state: { user: data.user } });
      } catch (e) {
        console.error('Google auth exchange failed', e);
        const detail = e?.response?.data?.detail;
        const reason = detail === 'sso_account_not_provisioned'
          ? 'sso_not_provisioned'
          : detail === 'account_disabled' ? 'account_disabled' : 'google_auth_failed';
        navigate(`/login?error=${reason}`, { replace: true });
      }
    })();
  }, [navigate]);

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/mfa-verify`,
        { challenge_token: mfa.challenge_token, code: code.trim() },
        { withCredentials: true }
      );
      if (data?.user) {
        localStorage.setItem('apex_user', JSON.stringify(data.user));
        localStorage.removeItem('apex_token');
      }
      navigate('/', { replace: true, state: { user: data.user } });
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      if (detail === 'challenge_expired' || detail === 'challenge_invalid') {
        setErr('Challenge expired — sign in with Google again.');
      } else {
        setErr('Invalid 2FA code — try a fresh code or a backup code.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (mfa) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }} data-testid="sso-mfa-step">
        <div className="panel p-6" style={{ width: 360 }}>
          <div className="flex items-center gap-2 mb-3 p-2 border" style={{ borderColor: 'var(--accent-amber)', color: 'var(--accent-amber)', fontSize: 11 }} data-testid="sso-mfa-banner">
            <ShieldCheck size={14} /> TWO-FACTOR REQUIRED · {mfa.email}
          </div>
          <form onSubmit={submitMfa} className="space-y-3">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={16}
              placeholder="000000"
              className="input-sharp"
              style={{ letterSpacing: '0.35em', textAlign: 'center', fontSize: 18 }}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
              required
              data-testid="sso-mfa-code-input"
            />
            {err && (
              <div className="flex items-center gap-2 p-2 border" style={{ borderColor: 'var(--accent-red)', color: 'var(--accent-red)', fontSize: 11 }} data-testid="sso-mfa-error">
                <AlertTriangle size={14} /> {err}
              </div>
            )}
            <button type="submit" className="btn-sharp btn-primary w-full" disabled={busy || code.trim().length < 6} data-testid="sso-mfa-submit">
              {busy ? 'VERIFYING…' : 'VERIFY 2FA CODE'}
            </button>
            <button type="button" className="label-xs underline w-full" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => navigate('/login', { replace: true })} data-testid="sso-mfa-cancel">
              CANCEL · BACK TO LOGIN
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }} data-testid="auth-callback">
      <div className="panel p-6 text-center">
        <div className="label-xs mb-2" style={{ color: 'var(--accent-amber)' }}>AUTHENTICATING</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>exchanging session…</div>
      </div>
    </div>
  );
}
